import React, { useCallback, useEffect, useMemo } from 'react';
import { TypedMemo } from '../common/types';
import { LoadingView } from '../common/views/LoadingView';
import { ErrorView } from '../common/views/ErrorView/ErrorView';
import { useTRPCClientAPI, useTRPCMutation } from '../../trpc/trpc';
import { useSMLRouter } from '../../routing/sml/hooks';
import { SMLRoutes } from '../../routing/sml/SMLRoutes';
import { useWorkspaces } from '../workspaces/useWorkspaces';
import { useSwitchWorkspace } from '../workspaces/useSwitchWorkspace';
import { useTeamInvitePreview } from './useTeamInvitePreview';
import { clearInviteToken } from './teamInviteToken';
import { readM24Error } from '../music24/utils/parseM24LimitError';
import { TeamAcceptInviteStep } from './TeamAcceptInviteStep';
import { TeamAcceptInvalidInvite } from './TeamAcceptInvalidInvite';
import { TeamAcceptCard } from './TeamAcceptCard';
import { teamAcceptInviteState } from './teamAcceptInviteState';
import { useFinishJoin } from './useFinishJoin';

interface TeamAcceptFlowProps {
  token: string;
}

/** The names the c8 toast shows, read at the click: the token swap empties `workspaces.list`. */
interface JoinNames {
  workspaceName: string;
  homeName: string | null;
}

// design: workspaces c1–c8 (Join Workspace). Joining never leaves the own workspace (spec §3).
const TeamAcceptFlowComponent: React.FC<TeamAcceptFlowProps> = ({ token }) => {
  const trpcClient = useTRPCClientAPI();
  const router = useSMLRouter();
  const preview = useTeamInvitePreview(token);
  const finishJoin = useFinishJoin();
  const { workspaces } = useWorkspaces();
  // c5: the switch clears the stored token, then opens Home with its own toast.
  const switcher = useSwitchWorkspace(() => clearInviteToken());
  const homeWorkspace = useMemo(() => workspaces.find((workspace) => workspace.isHome) ?? null, [workspaces]);
  const memberLicenseId = preview.data?.memberLicenseId ?? null;
  const memberWorkspace = useMemo(
    () => workspaces.find((workspace) => workspace.licenseId === memberLicenseId) ?? null,
    [memberLicenseId, workspaces]
  );

  const accept = useTRPCMutation({
    mutationFn: (_names: JoinNames) => trpcClient.team.accept.mutate({ token }),
    // The invitee cannot fix a full workspace by upgrading their own, so the global upgrade prompt
    // (opened for every tRPC mutation error by `TRPCLoader`'s `MutationCache`) would offer the wrong
    // checkout for `M24_LIMIT_SEATS` here. This opts the whole mutation out of it — `team.accept`
    // never throws any other `M24_LIMIT_*` code — and c6 covers it instead
    // (`dispatchM24LimitPromptForMutation`).
    meta: { skipM24LimitPrompt: true },
    onError: (error: unknown) => {
      // c5: only the preview knows the licence to open, so reload it.
      if (readM24Error(error)?.code === 'M24_TEAM_ALREADY_MEMBER') {
        preview.refetch();
      }
    },
    onSuccess: (result: { token: string }, names: JoinNames) => finishJoin.finish({ token: result.token, ...names }),
  });

  // c5: a lost membership (FORBIDDEN) reloads the preview, so the card falls back to c1.
  const lostMembership = switcher.failure?.lostMembership === true;
  const { refetch: refetchPreview } = preview;
  useEffect(() => {
    if (lostMembership) {
      refetchPreview();
    }
  }, [lostMembership, refetchPreview]);

  const errorCode = readM24Error(accept.error)?.code;
  const workspaceName = preview.data?.workspaceName ?? '';
  const homeName = homeWorkspace?.name ?? null;
  const join = useCallback(() => accept.mutate({ workspaceName, homeName }), [accept, homeName, workspaceName]);
  // c1 "Not now": it only leaves the page. The invite is not revoked; it expires on its own.
  const decline = useCallback(() => {
    clearInviteToken();
    router.replace(SMLRoutes.home);
  }, [router]);
  // c5: "Go to Home" when the invite workspace is the active one, else switch into it.
  const { switchTo } = switcher;
  const openWorkspace = useCallback(() => {
    if (memberWorkspace?.isActive === true || memberLicenseId === null) {
      decline();
      return;
    }
    switchTo(memberLicenseId, workspaceName);
  }, [decline, memberLicenseId, memberWorkspace, switchTo, workspaceName]);

  if (finishJoin.hasJoined) {
    return <LoadingView />;
  }
  if (finishJoin.error) {
    return <ErrorView error={finishJoin.error} retry={finishJoin.retry} />;
  }
  const isJoining = accept.isPending || finishJoin.joined !== null;
  const isCheckingMembership = errorCode === 'M24_TEAM_ALREADY_MEMBER' && preview.isFetching;
  if (preview.isLoading || isCheckingMembership || (isJoining && !preview.data)) {
    return <LoadingView />;
  }
  if (
    !isJoining &&
    (readM24Error(preview.error)?.code === 'M24_TEAM_INVITE_INVALID' || errorCode === 'M24_TEAM_INVITE_INVALID')
  ) {
    return <TeamAcceptInvalidInvite />;
  }
  if (!preview.data || (preview.error && !isJoining)) {
    return <ErrorView error={preview.error ?? undefined} retry={preview.refetch} />;
  }

  return (
    <TeamAcceptCard>
      <TeamAcceptInviteStep
        preview={preview.data}
        state={teamAcceptInviteState(preview.data, errorCode)}
        homeWorkspace={homeWorkspace}
        memberWorkspace={memberWorkspace}
        acceptError={accept.error}
        switchFailure={switcher.failure}
        isJoining={isJoining}
        isOpening={switcher.pendingLicenseId !== null}
        onDecline={decline}
        onJoin={join}
        onOpenWorkspace={openWorkspace}
      />
    </TeamAcceptCard>
  );
};

export const TeamAcceptFlow = TypedMemo(TeamAcceptFlowComponent);
